import { useAppForm } from '@/hooks/app-form';
import { useApiMutation } from '@/api/ApiQueryServiceProvider';
import {
  TestDeviceFormSchema,
  FORM_CONFIG,
  type TestDeviceFormData,
} from './schema';
import { FormConfigLoader } from '@/lib/formConfigLoader';
import {
  renderDateControl,
  renderRadioControl,
  renderButtonControl,
  type ControlConfig,
} from '../../formRenderFunctions';
import { AppLogger } from '@/lib/logger';

const log = AppLogger.getLogger(import.meta);

export { TestDeviceFormSchema, type TestDeviceFormData } from './schema';

const validateField = (name: keyof TestDeviceFormData, value: unknown): string | null => {
  const shape = TestDeviceFormSchema.shape[name];
  if (!shape) return null;

  const parsed = shape.safeParse(value);
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value';
  }
  return null;
};

export type TestDeviceComposerProps = {
  deviceNumber: number;
  deviceName?: string;
  deviceProcurementInfoNumber?: number;
  deviceProvisioningInfoNumber?: number;
  orderDate?: number | string;
  manufacturerName?: string;
  distributorName?: string;
  networkCorridorName?: string;
  networkSiteName?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
  showHeader?: boolean;
  submitLabel?: string;
};

export function TestDeviceComposer(props: TestDeviceComposerProps) {
  const {
    deviceNumber,
    deviceName,
    deviceProcurementInfoNumber,
    deviceProvisioningInfoNumber,
    orderDate,
    manufacturerName,
    distributorName,
    networkCorridorName,
    networkSiteName,
    onSuccess,
    onError,
    onCancel,
    showHeader = true,
    submitLabel,
  } = props;

  let controlsByGroup: Map<string, ControlConfig[]> = new Map();
  let groups: { formGroupName: string }[] = [];
  let formTitle = FORM_CONFIG.formTitle || 'Mark Device as Tested';
  try {
    controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG);
    // Patch: Map radioOptions to options for all controls
    for (const groupControls of controlsByGroup.values()) {
      groupControls.forEach((control) => {
        if ((control as any).radioOptions && !control.options) {
          control.options = (control as any).radioOptions;
        }
      });
    }
    groups = FormConfigLoader.getGroups(FORM_CONFIG);
  } catch (e) {
    controlsByGroup = new Map();
    groups = [];
  }

  // Mutations
  const getDevice = useApiMutation<{ device: any; procurementInfo?: any; provisioningInfo?: any }>('getDeviceByNumber', 'GET');
  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
  });
  const upsertProvisioning = useApiMutation('upsertDeviceProvisioningInfo', 'POST');
  const upsertProcurement = useApiMutation('upsertDeviceProcurementInfo', 'POST');

  const form = useAppForm<TestDeviceFormData>({
    schema: TestDeviceFormSchema,
    defaultValues: {
      deviceTestCompletionDate: new Date().toISOString().split('T')[0],
      nextStep: 'available',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as TestDeviceFormData
      // Log initial state for debugging
      log.debug('[testDevice] Starting workflow:', {
        deviceNumber,
        currentStatus: 'testing',
        nextStep: formValue.nextStep,
        hasProvisioningInfo: !!deviceProvisioningInfoNumber,
      });

      try {
        // Fetch current device aggregate to get complete device and procurement info
        const currentDevice = await getDevice.mutateAsync({ deviceNumber });
        const existingDevice: any = (currentDevice as any)?.device || {};
        const existingProcurementInfo: any = (currentDevice as any)?.procurementInfo || {};
        const existingProvisioningInfo: any = (currentDevice as any)?.provisioningInfo || {};

        // Build complete procurement info with all required fields
        const completeProcurementInfo: any = {
          deviceNumber,
          deviceProcurementInfoNumber,
          deviceTestingCompletedDate: new Date(formValue.deviceTestCompletionDate).getTime() / 1000,
          deviceProcurementStatus: 'tested',
        };

        // Selectively include other fields from existing procurement info if they exist and are valid
        if (existingProcurementInfo.deviceDistributorNumber && existingProcurementInfo.deviceDistributorNumber >= 1000000000) {
          completeProcurementInfo.deviceDistributorNumber = existingProcurementInfo.deviceDistributorNumber;
        }
        if (existingProcurementInfo.devicePurchaseOrderNumber && existingProcurementInfo.devicePurchaseOrderNumber >= 1000000000) {
          completeProcurementInfo.devicePurchaseOrderNumber = existingProcurementInfo.devicePurchaseOrderNumber;
        }
        if (existingProcurementInfo.deviceOrderDate) {
          completeProcurementInfo.deviceOrderDate = existingProcurementInfo.deviceOrderDate;
        }
        if (existingProcurementInfo.shipperName) {
          completeProcurementInfo.shipperName = existingProcurementInfo.shipperName;
        }
        if (existingProcurementInfo.deviceShippingDate) {
          completeProcurementInfo.deviceShippingDate = existingProcurementInfo.deviceShippingDate;
        }
        if (existingProcurementInfo.deviceShippingTrackingNumber) {
          completeProcurementInfo.deviceShippingTrackingNumber = existingProcurementInfo.deviceShippingTrackingNumber;
        }
        if (existingProcurementInfo.deviceDeliveryDate) {
          completeProcurementInfo.deviceDeliveryDate = existingProcurementInfo.deviceDeliveryDate;
        }
        if (existingProcurementInfo.deviceTypeNumber && existingProcurementInfo.deviceTypeNumber >= 1000000000) {
          completeProcurementInfo.deviceTypeNumber = existingProcurementInfo.deviceTypeNumber;
        }
        if (existingProcurementInfo.deviceShippedToAddressNumber && existingProcurementInfo.deviceShippedToAddressNumber >= 1000000000) {
          completeProcurementInfo.deviceShippedToAddressNumber = existingProcurementInfo.deviceShippedToAddressNumber;
        }

        // Step 1: Update procurement info using dedicated endpoint - THIS IS CRITICAL
        await upsertProcurement.mutateAsync({
          deviceNumber,
          procurementInfo: completeProcurementInfo
        });

        // Step 2: Transition device status to 'tested' (merge with existing device data)
        await upsertDevice.mutateAsync({
          device: {
            deviceNumber,
            deviceStatus: 'tested',
            deviceName: deviceName || existingDevice.deviceName || '',
            // Preserve reference numbers
            deviceTypeNumber: existingDevice.deviceTypeNumber || null,
            deviceKeyInfoNumber: existingDevice.deviceKeyInfoNumber || null,
            deviceProcurementInfoNumber: existingDevice.deviceProcurementInfoNumber || null,
            deviceProvisioningInfoNumber: existingDevice.deviceProvisioningInfoNumber || null,
          }
        });

        // Step 3: If marking as available, deprovision the device
        let deprovisioningResponse = null;
        if (formValue.nextStep === 'available' && deviceProvisioningInfoNumber) {
          deprovisioningResponse = await upsertProvisioning.mutateAsync({
            deviceNumber,
            provisioningInfo: {
              ...existingProvisioningInfo,
              deviceNumber,
              deviceProvisioningInfoNumber,
              deviceProvisioningStatus: 'deprovisioned',
              archiveDate: Math.floor(Date.now() / 1000),
            }
          });

          // Log for debugging
          log.debug('[testDevice] Deprovisioning response:', deprovisioningResponse);

          // Validate response
          if (!deprovisioningResponse?.content?.provisioningInfo?.deviceProvisioningInfoNumber) {
            log.warn('[testDevice] Deprovisioning succeeded but no provisioningInfoNumber returned');
          }
        }

        // Step 4: Transition from 'tested' to 'available' or 'live' (merge with existing device data)
        const finalDeviceResponse = await upsertDevice.mutateAsync({
          device: {
            deviceNumber,
            deviceStatus: formValue.nextStep,
            deviceName: deviceName || existingDevice.deviceName || '',
            // Preserve reference numbers
            deviceTypeNumber: existingDevice.deviceTypeNumber || null,
            deviceKeyInfoNumber: existingDevice.deviceKeyInfoNumber || null,
            deviceProcurementInfoNumber: existingDevice.deviceProcurementInfoNumber || null,
            deviceProvisioningInfoNumber: existingDevice.deviceProvisioningInfoNumber || null,
          }
        });

        // Log for debugging
        log.debug('[testDevice] Final device update response:', finalDeviceResponse);
        log.debug('[testDevice] Expected final status:', formValue.nextStep);

        // Validate the final status was set
        if (finalDeviceResponse?.content?.device?.deviceStatus !== formValue.nextStep) {
          log.error('[testDevice] Device status mismatch!', { expected: formValue.nextStep, got: finalDeviceResponse?.content?.device?.deviceStatus });
        }

        // VALIDATION: Ensure all steps completed successfully
        const validationErrors: string[] = [];

        if (!finalDeviceResponse?.content?.device) {
          validationErrors.push('Final device update returned no device data');
        }

        if (finalDeviceResponse?.content?.device?.deviceStatus !== formValue.nextStep) {
          validationErrors.push(`Device status mismatch: expected ${formValue.nextStep}, got ${finalDeviceResponse?.content?.device?.deviceStatus}`);
        }

        if (formValue.nextStep === 'available' && deviceProvisioningInfoNumber && !deprovisioningResponse) {
          validationErrors.push('Device marked as available but deprovisioning was not performed');
        }

        if (validationErrors.length > 0) {
          log.error('[testDevice] Validation errors:', validationErrors);
          throw new Error('Workflow validation failed: ' + validationErrors.join('; '));
        }

        // All transitions succeeded - log final state
        log.debug('[testDevice] Workflow completed successfully:', {
          deviceNumber,
          finalStatus: formValue.nextStep,
          procurementStatus: 'tested',
          provisioningDeprovisioned: formValue.nextStep === 'available',
        });

        if (onSuccess) onSuccess();
      } catch (error) {
        // Handle errors from any transition with detailed logging
        log.error('[testDevice] Failed to complete device testing workflow:', error);
        log.error('[testDevice] Current state may be inconsistent - check device status');

        if (onError) {
          onError(error as Error);
        } else {
          // If no error handler, ensure error is visible to user
          log.error('[testDevice] No onError handler provided, error may be swallowed');
        }

        // Don't re-throw - let TanStack Form handle the error state
        // throw error;  // REMOVED
      }
    },
  });

  // Render a single control using AppField and bound pattern (like addDevice)
  const renderControl = (control: ControlConfig) => {
    if (control.htmlControl === 'date') {
      return (
        <form.AppField
          key={control.name}
          name={control.name as 'deviceTestCompletionDate'}
          validators={{
            onBlur: ({ value }) =>
              validateField(control.name as keyof TestDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => renderDateControl(control, bound)}
        </form.AppField>
      );
    }

    if (control.htmlControl === 'radio') {
      // Build options with descriptions
      const optionsWithDescriptions = control.options?.map((option) => {
        if (option.value === 'available') {
          return {
            ...option,
            description: 'Device will be deprovisioned from current site and marked as Available for future provisioning.'
          };
        }
        if (option.value === 'live' && networkCorridorName && networkSiteName) {
          return {
            ...option,
            description: `${networkCorridorName} - ${networkSiteName}`
          };
        }
        return option;
      }) || [];

      return (
        <form.AppField
          key={control.name}
          name={control.name as 'nextStep'}
          validators={{
            onBlur: ({ value }) =>
              validateField(control.name as keyof TestDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => renderRadioControl({ ...control, options: optionsWithDescriptions }, bound)}
        </form.AppField>
      );
    }

    // fallback: just show value
    return (
      <form.AppField key={control.name} name={control.name as 'deviceTestCompletionDate' | 'nextStep'}>
        {(bound) => <span>{String(bound.state.value ?? '')}</span>}
      </form.AppField>
    );
  };


  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || [];
      if (groupControls.length === 0) return null;
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map((control) => (
            <div key={control.name}>{renderControl(control)}</div>
          ))}
        </div>
      );
    });
  };


  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{formTitle}</h2>
            {FORM_CONFIG.formDescription && (
              <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
            )}
          </div>
        )}

        {/* Info Section */}
        <div className="rounded-md bg-muted p-3 text-sm mb-2">
          <p><strong>Device Number:</strong> {deviceNumber}</p>
          {deviceName && <p><strong>Device Name:</strong> {deviceName}</p>}
          {orderDate && <p><strong>Order Date:</strong> {typeof orderDate === 'string' ? orderDate : new Date(orderDate * 1000).toLocaleDateString()}</p>}
          {/* A device is ordered from EITHER a manufacturer OR a distributor — show only the
              one order source: distributor if it has one, otherwise the manufacturer. */}
          {distributorName
            ? <p><strong>Distributor:</strong> {distributorName}</p>
            : manufacturerName && <p><strong>Manufacturer:</strong> {manufacturerName}</p>}
        </div>


        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}
          {renderButtonControl({
            label: (upsertDevice.isPending || upsertProvisioning.isPending || upsertProcurement.isPending)
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Testing...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Complete Test'),
            variant: 'default',
            type: 'submit',
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  );
}
